import { ArtistName } from '../types/Artist'
import {
  Dimensions,
  Unit,
  Duration,
  Edition,
  ComputedConfidenceLevel,
  ProductionDating,
} from '../types/Certificate'
import { ArtistAttributes } from '../types/Artist'
import t from './../libs/translation'
import { DateTime } from 'luxon'
import { Blockchain } from '@verisart/nft/src'
import { ObjectType } from '../types/Certificate'

function n(num: number | string): string {
  return num.toString()
}

export const dimensionString = (dimensions: Dimensions): string => {
  return `${dimensions.height} × ${dimensions.width}${
    dimensions.depth ? ` × ${dimensions.depth}` : ''
  } ${
    dimensions.unit === Unit.PIXEL
      ? 'px'
      : dimensions.unit === Unit.INCH
      ? 'inches'
      : dimensions.unit.substr(0, 2).toLowerCase()
  }`
}

export const durationString = (duration: Duration): string => {
  const mins = duration.hours * 60 + duration.minutes
  return `${mins}:${duration.seconds.toString().padStart(2, '0')} min`
}

export const editionString = (edition: Edition, short?: boolean): string => {
  // See https://docs.google.com/spreadsheets/d/15668sIkT4VuSKWkYKJje-Qby3l95Hpzsep2WQzS7rsM/edit#gid=0
  // !NOTE! The `core` project has some similar code in VerisartCertificateService which is required for
  // formatting emails. So if you make changes here, do the same there too.

  // Numbered edition
  if (edition.number && edition.number != -1) {
    if (!edition.apVolume) {
      if (edition.volume) {
        if (short) {
          return `${n(edition.number)}/${n(edition.volume)}`
        } else {
          return `Edition ${n(edition.number)}/${n(edition.volume)}`
        }
      } else {
        if (short) {
          return `${n(edition.number)}`
        } else {
          return `Edition ${n(edition.number)} / Open Edition`
        }
      }
    } else {
      if (edition.volume) {
        if (short) {
          return `${n(edition.number)}/${n(edition.volume)}`
        } else {
          return `Edition ${n(edition.number)}/${n(edition.volume)}, ${n(
            edition.apVolume
          )} AP${edition.apVolume > 1 ? 's' : ''}`
        }
      } else {
        if (short) {
          return `${n(edition.number)}`
        } else {
          return `Edition ${n(edition.number)} / Open Edition, ${n(
            edition.apVolume
          )} AP${edition.apVolume > 1 ? 's' : ''}`
        }
      }
    }
  }

  // Unnumbered edition
  if (edition.number && edition.number == -1) {
    if (!edition.apVolume) {
      if (edition.volume) {
        if (short) {
          return `Unnumbered`
        } else {
          return `Edition of ${n(edition.volume)}`
        }
      } else {
        return `Open Edition`
      }
    } else {
      if (edition.volume) {
        if (short) {
          return `Unnumbered`
        } else {
          return `Edition of ${n(edition.volume)}, ${n(edition.apVolume)} AP${
            edition.apVolume > 1 ? 's' : ''
          }`
        }
      } else {
        if (short) {
          return `Unnumbered`
        } else {
          return `Open Edition, ${n(edition.apVolume)} AP${
            edition.apVolume > 1 ? 's' : ''
          }`
        }
      }
    }
  }

  // Numbered AP
  if (edition.apNumber && edition.apNumber != -1) {
    if (!edition.apVolume) {
      if (edition.volume) {
        if (short) {
          return `AP ${n(edition.apNumber)}`
        } else {
          return `AP ${n(edition.apNumber)}, Edition of ${n(edition.volume)}`
        }
      } else {
        return `AP ${n(edition.apNumber)}`
      }
    } else {
      if (edition.volume) {
        if (short) {
          return `AP ${n(edition.apNumber)}/${n(edition.apVolume)}`
        } else {
          return `AP ${n(edition.apNumber)}/${n(
            edition.apVolume
          )}, Edition of ${n(edition.volume)}`
        }
      } else {
        return `AP ${n(edition.apNumber)}/${n(edition.apVolume)}`
      }
    }
  }

  // Unnumbered AP
  if (edition.apNumber && edition.apNumber == -1) {
    if (!edition.apVolume) {
      if (edition.volume) {
        if (short) {
          return `AP`
        } else {
          return `AP, Edition of ${n(edition.volume)}`
        }
      } else {
        return `AP`
      }
    } else {
      if (edition.volume) {
        if (short) {
          return `AP of ${n(edition.apVolume)}`
        } else {
          return `AP of ${n(edition.apVolume)}, Edition of ${n(edition.volume)}`
        }
      } else {
        return `AP of ${n(edition.apVolume)}`
      }
    }
  }

  throw Error("Don't know how to render edition string")
}

export function showProductionDating(
  productionDating: ProductionDating | null | undefined
): boolean {
  return !!productionDating
}

export function formatProductionDatingOrNull(
  productionDating: ProductionDating | null | undefined,
  showComma: boolean = false
): string | null {
  if (!showProductionDating(productionDating)) {
    return null
  }
  return productionDating
    ? showComma
      ? `, ${formatProductionDating(productionDating)}`
      : formatProductionDating(productionDating)
    : null
}

export function formatProductionDating(
  productionDating: ProductionDating
): string {
  const { undated, circa, range, primaryYear, secondaryYear } = productionDating
  if (undated) {
    return 'n.d.'
  }

  if (circa && primaryYear) {
    const yearStr = primaryYear.year.toString()
    return primaryYear.attributed ? `[c. ${yearStr}]` : `c. ${yearStr}`
  }

  if (range && primaryYear && secondaryYear) {
    return primaryYear.attributed
      ? `[${primaryYear.year}–${secondaryYear.year}]`
      : `${primaryYear.year}–${secondaryYear.year}`
  }

  if (primaryYear && secondaryYear) {
    const firstStr =
      primaryYear.attributed && !secondaryYear.attributed
        ? `[${primaryYear.year}]`
        : primaryYear.year.toString()
    const secondStr =
      secondaryYear.attributed && !primaryYear.attributed
        ? `[${secondaryYear.year}]`
        : secondaryYear.year.toString()
    return primaryYear.attributed && secondaryYear.attributed
      ? `[${firstStr}/${secondStr}]`
      : `${firstStr}/${secondStr}`
  }

  if (primaryYear) {
    return primaryYear.attributed
      ? `[${primaryYear.year}]`
      : primaryYear.year.toString()
  }

  return ''
}

/**
 * Parses a production dating string into a ProductionDating object.
 * Supports various formats:
 * - Single year: "2000" or "[2000]" (attributed)
 * - Circa: "c. 2000" or "[c. 2000]" (attributed)
 * - Range: "2000–2005" or "[2000–2005]" (attributed)
 * - Distinct years: "2000/2005" or "[2000]/[2005]" (attributed)
 * - Undated: "n.d." or "n.d"
 *
 * @param dateString - The production dating string to parse
 * @returns ProductionDating object or undefined if parsing fails
 */
export function parseProductionDating(
  dateString?: string
): ProductionDating | undefined {
  if (!dateString) return undefined

  const trimmed = dateString.trim()

  // Handle undated
  if (trimmed.toLowerCase() === 'n.d.' || trimmed.toLowerCase() === 'n.d') {
    return {
      primaryYear: null,
      secondaryYear: null,
      range: false,
      circa: false,
      undated: true,
    }
  }

  // Check if the entire string is attributed (wrapped in brackets)
  const isFullyAttributed = trimmed.startsWith('[') && trimmed.endsWith(']')
  const cleanString = isFullyAttributed ? trimmed.slice(1, -1).trim() : trimmed

  // Handle circa dates: "c. 2000" or "[c. 2000]"
  const circaMatch = cleanString.match(/^c\.\s*(\d+)$/i)
  if (circaMatch) {
    const year = parseInt(circaMatch[1], 10)
    if (!isNaN(year)) {
      return {
        primaryYear: {
          year,
          attributed: isFullyAttributed,
        },
        secondaryYear: null,
        range: false,
        circa: true,
        undated: false,
      }
    }
  }

  // Handle ranges: "2000–2005" or "[2000–2005]"
  // Support both en dash (–) and hyphen (-)
  const rangeMatch = cleanString.match(/^(\d+)\s*[–-]\s*(\d+)$/)
  if (rangeMatch) {
    const startYear = parseInt(rangeMatch[1], 10)
    const endYear = parseInt(rangeMatch[2], 10)
    if (!isNaN(startYear) && !isNaN(endYear) && endYear > startYear) {
      return {
        primaryYear: {
          year: startYear,
          attributed: isFullyAttributed,
        },
        secondaryYear: {
          year: endYear,
          attributed: isFullyAttributed,
        },
        range: true,
        circa: false,
        undated: false,
      }
    }
  }

  // Handle distinct years: "2000/2005" or "[2000]/[2005]"
  // Also handle "[2000]/2005" or "2000/[2005]" (mixed attribution)
  const distinctMatch = cleanString.match(/^(\[?\d+\]?)\s*\/\s*(\[?\d+\]?)$/)
  if (distinctMatch) {
    const firstPart = distinctMatch[1].trim()
    const secondPart = distinctMatch[2].trim()
    const firstAttributed = firstPart.startsWith('[') && firstPart.endsWith(']')
    const secondAttributed =
      secondPart.startsWith('[') && secondPart.endsWith(']')
    const firstYear = parseInt(
      firstAttributed ? firstPart.slice(1, -1) : firstPart,
      10
    )
    const secondYear = parseInt(
      secondAttributed ? secondPart.slice(1, -1) : secondPart,
      10
    )

    if (!isNaN(firstYear) && !isNaN(secondYear) && secondYear > firstYear) {
      return {
        primaryYear: {
          year: firstYear,
          attributed: firstAttributed || isFullyAttributed,
        },
        secondaryYear: {
          year: secondYear,
          attributed: secondAttributed || isFullyAttributed,
        },
        range: false,
        circa: false,
        undated: false,
      }
    }
  }

  // Handle single year: "2000" or "[2000]"
  const singleYearMatch = cleanString.match(/^(\d+)$/)
  if (singleYearMatch) {
    const year = parseInt(singleYearMatch[1], 10)
    if (!isNaN(year)) {
      return {
        primaryYear: {
          year,
          attributed: isFullyAttributed,
        },
        secondaryYear: null,
        range: false,
        circa: false,
        undated: false,
      }
    }
  }

  // If no pattern matches, return undefined
  return undefined
}

export function formatArtistName(name: ArtistName): string {
  if (name?.lastName) {
    return `${name.firstName} ${name.lastName}`
  }
  return name?.firstName
}

export function capitalizeFirstLetter(value: string) {
  return value.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  })
}

export const certificateType = (confidenceLevel: ComputedConfidenceLevel) => {
  if (confidenceLevel === ComputedConfidenceLevel['UNVERIFIED_RECORD']) {
    return t('certificateTypes.UNVERIFIED_RECORD')
  } else if (confidenceLevel === ComputedConfidenceLevel['ISSUER_RECORD']) {
    return t('certificateTypes.ISSUER_RECORD')
  } else if (confidenceLevel === ComputedConfidenceLevel['COA']) {
    return t('certificateTypes.COA')
  } else if (confidenceLevel === ComputedConfidenceLevel['COA_PLUS']) {
    return t('certificateTypes.COA_PLUS')
  }

  return t('certificateTypes.UNVERIFIED_RECORD')
}

export function truncateString(
  str: string,
  firstCharCount: number,
  endCharCount: number
): string {
  if (str.length <= firstCharCount + endCharCount) return str

  let convertedStr = ''
  convertedStr += str.substring(0, firstCharCount)
  convertedStr += '…'
  convertedStr += str.substring(str.length - endCharCount, str.length)
  return convertedStr
}

export function truncateEthereumAddress(str: string): string {
  return truncateString(str, 6, 4)
}

export const presentBlockchainDate = (isoDateTime: string): string =>
  DateTime.fromISO(isoDateTime)
    .setZone('utc')
    .toFormat('LLL d yyyy HH:mm:ss ZZZZ')

export function formatArtistDetails(artist: ArtistAttributes): string {
  const name = formatArtistName(artist.name)
  const birth = artist.yearOfBirth ? ` (${artist.yearOfBirth})` : ''
  const alternativeNames =
    artist.alternativeNames.length > 0
      ? ` (${artist.alternativeNames.map(formatArtistName).join()})`
      : ''
  return `${name}${birth}${alternativeNames}`
}

export function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
}

export const blockchainName = (blockchain: Blockchain) => {
  return t(`blockchainShortName.${blockchain}`)
}

export function formatObjectType(
  objectType: ObjectType | undefined
): string | null {
  if (!objectType) return null

  return objectType === 'NFT'
    ? objectType
    : capitalizeFirstLetter(objectType.replace('_', ' '))
}

export function stripNFTNumber(name: string) {
  // Remove the NFT number from the name. Eg Halcyon #2570 -> Halcyon
  return name.replace(/\s*#\d+$/, '')
}
